import { useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import { activeChild, blocksForDate, reviewFor, type PlanBlock } from '../../store/selectors'
import { formatRelative } from '../../lib/dates'

/**
 * The evening check-in.
 *
 * On paper this was the moment the checksheet got its ticks and its margin
 * notes. Here it is one panel: how many are marked, one field for anything
 * else from the day, and one button. Closing the day marks whatever is still
 * unmarked as skipped — an honest record beats a hopeful one — and sends every
 * note through as evidence.
 */
export function CloseDay({ date, today }: { date: string; today: string }) {
  const child = useAuroraStore(activeChild)
  const blocks = useAuroraStore((s: any) => blocksForDate(s, date)) as PlanBlock[]
  const review = useAuroraStore((s: any) => reviewFor(s, 'day', date))
  const [notes, setNotes] = useState('')

  if (!child || blocks.length === 0 || date > today) return null

  const closed = !!review?.parent?.submittedAt
  const marked = blocks.filter((b) => b.status && b.status !== 'pending').length
  const noted = blocks.filter((b) => b.note).length

  const close = () => {
    const s = auroraStore.getState() as any
    s.closeDay({
      childId: (child as any)._id,
      date,
      blocks: blocks.map((b) => ({
        blockId: b.id,
        status: (b.status && b.status !== 'pending' ? b.status : 'skipped') as 'done' | 'partial' | 'skipped',
        note: b.note,
      })),
      notes,
    })
    setNotes('')
  }

  if (closed) {
    return (
      <section className="section closeday is-closed">
        <div className="closeday-row">
          <span className="closeday-check" aria-hidden="true"><CheckIcon /></span>
          <span className="closeday-text">
            <span className="closeday-title">Day closed</span>
            <span className="closeday-sub">
              {formatRelative(review!.parent!.submittedAt!)} · {marked} of {blocks.length} marked
              {noted ? `, ${noted} note${noted === 1 ? '' : 's'}` : ''}
              {review?.parent?.notes ? ` · “${review.parent.notes}”` : ''}
            </span>
          </span>
        </div>
      </section>
    )
  }

  return (
    <section className="section closeday">
      <div className="section-head">
        <h2 className="section-title">{date === today ? 'Close the day' : 'Close this day'}</h2>
        <span className="rituals-count tnum">{marked} of {blocks.length} marked</span>
      </div>
      <p className="closeday-lead">
        Anything left unmarked is recorded as skipped. Notes become part of the record.
      </p>
      <textarea
        className="closeday-notes"
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Anything else from today? What she said, what she loved, what fell flat…"
      />
      <div className="row row-2" style={{ marginTop: 'var(--s-3)' }}>
        <button className="btn btn-primary" onClick={close}>Close the day</button>
        <span className="closeday-hint muted">Takes ten seconds. Aurora reads every note.</span>
      </div>
    </section>
  )
}

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12.5l5 5L20 6.5" /></svg>
)
